/**
 * RealtimeManager — مدير Supabase Realtime المركزي
 * يستمع لكل جداول قاعدة البيانات ويطلق أحداث window عند أي تغيير
 * subscription واحدة لكل الجداول — لا تكرار
 */

import { supabase } from "@/lib/supabase";

// خريطة الجداول → أسماء الأحداث
const TABLE_EVENTS: Record<string, string> = {
  articles: "alsiyahiah:articles:updated",
  videos: "alsiyahiah:videos:updated",
  infographics: "alsiyahiah:infographics:updated",
  opinion_articles: "alsiyahiah:opinion:updated",
  categories: "alsiyahiah:categories:updated",
  team_members: "alsiyahiah:about:updated",
  milestones: "alsiyahiah:about:updated",
  about_info: "alsiyahiah:about:updated",
  ad_placements: "alsiyahiah:adplacements:updated",
  ad_requests: "alsiyahiah:adrequests:updated",
  gcc_listings: "alsiyahiah:gcc:updated",
  gcc_countries: "alsiyahiah:gcc:updated",
  newspaper_editions: "alsiyahiah:newspaper:updated",
  pdf_subscribers: "alsiyahiah:pdfsubscribers:updated",
  site_settings: "alsiyahiah:settings:updated",
  social_links: "alsiyahiah:sociallinks:updated",
};

let initialized = false;
let channelRef: ReturnType<typeof supabase.channel> | null = null;
let retryCount = 0;
const MAX_RETRIES = 3;
let retryTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * يُشغَّل مرة واحدة فقط عند بدء التطبيق
 * يُنشئ channel واحد يستمع لكل الجداول
 */
export function initRealtimeManager(): void {
  if (initialized) return;
  initialized = true;

  const channel = supabase.channel("alsiyahiah-global-realtime", {
    config: { broadcast: { self: true } },
  });
  channelRef = channel;

  Object.keys(TABLE_EVENTS).forEach((table) => {
    channel.on(
      "postgres_changes" as Parameters<typeof channel.on>[0],
      { event: "*", schema: "public", table },
      (payload: unknown) => {
        const eventName = TABLE_EVENTS[table];
        window.dispatchEvent(new CustomEvent(eventName, { detail: payload }));
      }
    );
  });

  channel.subscribe((status) => {
    if (status === "SUBSCRIBED") {
      console.log("[Realtime] Connected — listening to", Object.keys(TABLE_EVENTS).length, "tables");
      retryCount = 0; // إعادة العداد عند النجاح
    } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
      if (retryCount >= MAX_RETRIES) {
        console.warn("[Realtime] Max retries reached — giving up");
        return;
      }
      retryCount++;
      // Exponential backoff: 3s, 6s, 12s
      const delay = 3000 * Math.pow(2, retryCount - 1);
      console.warn(`[Realtime] Connection issue: ${status} — retry ${retryCount}/${MAX_RETRIES} in ${delay}ms`);
      if (retryTimer) clearTimeout(retryTimer);
      retryTimer = setTimeout(() => {
        initialized = false;
        channelRef = null;
        initRealtimeManager();
      }, delay);
    }
  });
}

/**
 * إعادة الاتصال يدوياً (مفيد عند عودة الإنترنت)
 */
export function reconnectRealtime(): void {
  if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
  if (channelRef) {
    supabase.removeChannel(channelRef);
    channelRef = null;
  }
  initialized = false;
  retryCount = 0;
  initRealtimeManager();
}

/**
 * Hook مساعد: يستمع لحدث window ويُشغّل callback عند التغيير
 * يُستخدم في الـ stores والـ hooks
 */
export function onRealtimeEvent(
  eventName: string,
  callback: () => void
): () => void {
  window.addEventListener(eventName, callback);
  return () => window.removeEventListener(eventName, callback);
}
